import { useTranslation } from 'react-i18next'
import { useInstance } from '../hooks/useInstance.ts'

/**
 * The instance panel (law-c8ee3d8): which build is deployed here, answered in
 * the browser instead of over SSH.
 *
 * The version is rendered in a monospace face because it is an identifier to be
 * read character by character and compared against a registry tag — `0.4.1` and
 * `0.4.11` must not look alike at a glance.
 */
function Row({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border/40 py-2.5 last:border-0">
      <dt className="text-sm text-muted">{label}</dt>
      <dd className={`text-sm text-foreground ${mono ? 'font-mono' : ''}`}>{value}</dd>
    </div>
  )
}

export function SettingsPage() {
  const { t } = useTranslation()
  const { instance, error, isLoading } = useInstance()

  return (
    <div className="flex h-full flex-col">
      <header className="mb-6 border-b border-border/60 pb-4">
        <h1 className="text-xl font-semibold text-foreground">{t('nav.settings')}</h1>
        <p className="mt-1 text-sm text-muted">{t('pages.settings.description')}</p>
      </header>

      <section className="max-w-xl rounded-md border border-border/60 p-4">
        <h2 className="mb-2 text-sm font-semibold text-foreground">
          {t('pages.settings.instance.title', { defaultValue: 'Instance' })}
        </h2>

        {isLoading && (
          <p className="py-2 text-sm text-muted">
            {t('pages.settings.instance.loading', { defaultValue: 'Reading the instance…' })}
          </p>
        )}

        {error != null && (
          <p className="py-2 text-sm text-danger">
            {t('pages.settings.instance.error', {
              defaultValue: 'The engine did not answer. It may be mid-deploy.',
            })}
          </p>
        )}

        {instance && (
          <dl>
            <Row
              label={t('pages.settings.instance.tenant', { defaultValue: 'Tenant' })}
              value={instance.tenant}
            />
            <Row
              label={t('pages.settings.instance.version', { defaultValue: 'Deployed version' })}
              value={instance.version}
              mono
            />
            {instance.publicUrl != null && (
              <Row
                label={t('pages.settings.instance.publicUrl', { defaultValue: 'Public URL' })}
                value={instance.publicUrl}
              />
            )}
            <Row
              label={t('pages.settings.instance.scheduler', { defaultValue: 'Safety-net tick' })}
              value={
                instance.scheduler ??
                t('pages.settings.instance.schedulerOff', { defaultValue: 'off' })
              }
              mono={instance.scheduler != null}
            />
          </dl>
        )}

        {/* `dev` means the image was built without its tag — the state law-c8ee3d8
            was enacted over. Saying so here is cheaper than rediscovering it. */}
        {instance?.version === 'dev' && (
          <p className="mt-3 text-xs text-muted">
            {t('pages.settings.instance.devHint', {
              defaultValue:
                'This image was built without a version argument, so it cannot say which build it is.',
            })}
          </p>
        )}
      </section>
    </div>
  )
}
